/** payables · obrigações, parcelas e rateio (spec 02 §8 · tela 6.4). */
import { z } from 'zod'
import { chaveDeComando, codigoDeMoeda, dataNominal, dinheiroNaoNegativo, dinheiroPositivo, uuid } from './comum.js'
import { documentoExigidoDaDespesa, naturezaDeFornecedor } from './fornecedores.js'
import { tipoDeDiferenca } from './recebiveis.js'

/*
  O dinheiro de entrada mora em `comum.ts` desde a E1 do pacote mestre: o cadastro (capacidade de
  parceiro) passou a precisar dele, e importar daqui fecharia o ciclo cadastro → contas-pagar →
  fornecedores → cadastro. O reexport mantém os importadores de sempre.
*/
export { dinheiroNaoNegativo, dinheiroPositivo }

export const estadoDaParcelaPagar = z.enum([
  'nf_pendente', 'nf_registrada', 'aguardando_aprovacao', 'aprovada', 'paga', 'cancelada',
])
export type EstadoDaParcelaPagar = z.infer<typeof estadoDaParcelaPagar>

export const agendaDaContaPagar = z.enum(['unica', 'parcelada', 'recorrente'])
export type AgendaDaContaPagar = z.infer<typeof agendaDaContaPagar>

export const recorrenciaDeContaPagar = z.object({
  periodicidade: z.literal('mensal'),
  inicio: z.string().regex(/^\d{4}-\d{2}$/),
  fim: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
  diaVencimento: z.number().int().min(1).max(28),
  valor: dinheiroPositivo,
})
export type RecorrenciaDeContaPagar = z.infer<typeof recorrenciaDeContaPagar>

export const entradaDeRateioDaContaPagar = z.object({
  pedidoId: uuid.nullable().optional(),
  campanhaId: uuid.nullable().optional(),
  contaContabilId: uuid.nullable().optional(),
  centroCustoId: uuid.nullable().optional(),
  itemContabilId: uuid.nullable().optional(),
  valor: dinheiroPositivo,
}).refine((r) => [r.pedidoId, r.campanhaId, r.contaContabilId, r.centroCustoId, r.itemContabilId]
  .some((v) => v !== undefined && v !== null), {
  message: 'Cada linha de rateio precisa apontar pelo menos uma dimensão.',
})
export type EntradaDeRateioDaContaPagar = z.infer<typeof entradaDeRateioDaContaPagar>

export const entradaDeParcelaPagar = z.object({
  vencimento: dataNominal,
  base: dinheiroPositivo,
  bonus: dinheiroNaoNegativo.default('0'),
  deducao: dinheiroNaoNegativo.default('0'),
  deducaoTipo: z.string().trim().min(1).nullable().optional(),
}).superRefine((parcela, contexto) => {
  const temDeducao = dinheiroPositivo.safeParse(parcela.deducao).success
  const tipo = parcela.deducaoTipo ?? null
  if (temDeducao && (tipo === null || tipo === 'Sem dedução')) {
    contexto.addIssue({
      code: 'custom',
      path: ['deducaoTipo'],
      message: 'Nomeie a dedução aplicada à parcela.',
    })
  }
  if (!temDeducao && tipo !== null && tipo !== 'Sem dedução') {
    contexto.addIssue({
      code: 'custom',
      path: ['deducaoTipo'],
      message: 'Uma dedução nomeada precisa ter valor maior que zero.',
    })
  }
})
export type EntradaDeParcelaPagar = z.infer<typeof entradaDeParcelaPagar>

const baseDaContaPagar = z.object({
  fornecedorCapacidadeId: uuid.nullable().optional(),
  credorDescricao: z.string().trim().min(1).nullable().optional(),
  tipoPagamentoId: uuid,
  descricao: z.string().trim().min(1),
  competencia: dataNominal,
  moeda: codigoDeMoeda,
  contaContabilId: uuid,
  centroCustoId: uuid,
  itemContabilId: uuid.nullable().optional(),
  orcamentoLinhaId: uuid.nullable().optional(),
  rateio: z.array(entradaDeRateioDaContaPagar).default([]),
})

export const entradaDeContaPagar = baseDaContaPagar.and(z.discriminatedUnion('agenda', [
  z.object({
    agenda: z.literal('unica'),
    parcelas: z.tuple([entradaDeParcelaPagar]),
    recorrencia: z.null().optional(),
  }),
  z.object({
    agenda: z.literal('parcelada'),
    parcelas: z.array(entradaDeParcelaPagar).min(2).max(36),
    recorrencia: z.null().optional(),
  }),
  z.object({
    agenda: z.literal('recorrente'),
    parcelas: z.array(entradaDeParcelaPagar).min(1).max(36),
    recorrencia: recorrenciaDeContaPagar,
  }),
])).refine((e) => {
  const fornecedor = e.fornecedorCapacidadeId !== undefined && e.fornecedorCapacidadeId !== null
  const avulso = e.credorDescricao !== undefined && e.credorDescricao !== null
  return fornecedor !== avulso
}, { message: 'Escolha um fornecedor cadastrado ou informe um credor avulso.', path: ['fornecedorCapacidadeId'] })
export type EntradaDeContaPagar = z.infer<typeof entradaDeContaPagar>

/** Um vínculo do rateio da despesa: o pedido ou a campanha, com o código que a tela mostra. */
export const vinculoDaContaPagar = z.object({
  tipo: z.enum(['pedido', 'campanha']),
  id: uuid,
  codigo: z.string(),
})
export type VinculoDaContaPagar = z.infer<typeof vinculoDaContaPagar>

export const linhaDeContaPagar = z.object({
  id: uuid,
  contaPagarId: uuid,
  codigo: z.string(),
  fornecedorCapacidadeId: uuid.nullable(),
  fornecedorNome: z.string(),
  fornecedorNatureza: z.enum(['PJ', 'PF', 'Avulso']),
  descricao: z.string(),
  competencia: dataNominal,
  agenda: agendaDaContaPagar,
  contaContabilNome: z.string(),
  /** A âncora do Plano de Contas é o código, e não o uuid (D-136, DS-538). */
  contaContabilCodigo: z.string(),
  centroCustoNome: z.string(),
  itemContabilNome: z.string().nullable(),
  /**
   * Os pedidos e as campanhas do rateio, cada um com a ficha que o código abre (D-136,
   * DS-538). SUBSTITUI o antigo `vinculoCodigo` (os códigos unidos num texto): um
   * conceito, um lugar. Ordem pelo código; lista vazia = sem vínculo (overhead).
   */
  vinculos: z.array(vinculoDaContaPagar),
  numero: z.number().int(),
  totalParcelas: z.number().int(),
  vencimento: dataNominal,
  base: z.string(),
  bonus: z.string(),
  deducao: z.string(),
  deducaoTipo: z.string().nullable(),
  liquido: z.string(),
  moeda: codigoDeMoeda,
  status: estadoDaParcelaPagar,
  /**
   * O documento que a parcela espera antes da aprovação, lido do tipo de pagamento ATUAL:
   * `rpa` quando o tipo exige só o RPA, `nf` nos demais que exigem documento, e `null`
   * quando o tipo não exige nenhum (spec 6.4 R1, DS-555). Muda o rótulo, nunca o status.
   */
  documentoExigido: documentoExigidoDaDespesa.nullable(),
  vencida: z.boolean(),
  alcadaNome: z.string().nullable(),
})
export type LinhaDeContaPagar = z.infer<typeof linhaDeContaPagar>

export const agregadosDeContasPagar = z.object({
  aPagar: z.string(),
  aguardandoAprovacao: z.string(),
  vencidas: z.string(),
  pagas: z.string(),
})
export type AgregadosDeContasPagar = z.infer<typeof agregadosDeContasPagar>

export const rateioDaContaPagar = z.object({
  id: uuid,
  pedidoId: uuid.nullable(),
  pedidoCodigo: z.string().nullable(),
  campanhaId: uuid.nullable(),
  campanhaCodigo: z.string().nullable(),
  contaContabilId: uuid.nullable(),
  contaContabilNome: z.string().nullable(),
  /** O código da conta da linha, para a âncora do Plano de Contas (D-136, DS-538). */
  contaContabilCodigo: z.string().nullable(),
  centroCustoId: uuid.nullable(),
  centroCustoNome: z.string().nullable(),
  itemContabilId: uuid.nullable(),
  itemContabilNome: z.string().nullable(),
  valor: z.string(),
})
export type RateioDaContaPagar = z.infer<typeof rateioDaContaPagar>

export const liquidacaoDaContaPagar = z.object({
  id: uuid,
  data: dataNominal,
  valor: z.string(),
  contaBancariaId: uuid,
  contaBancariaNome: z.string(),
  forma: z.string().nullable(),
  movimentoBancarioId: uuid.nullable(),
})
export type LiquidacaoDaContaPagar = z.infer<typeof liquidacaoDaContaPagar>

export const obrigacaoDeDeducao = z.object({
  id: uuid,
  tributo: z.string(),
  valor: z.string(),
  vencimentoGuia: dataNominal,
  contaPagarId: uuid,
  contaPagarCodigo: z.string(),
})
export type ObrigacaoDeDeducao = z.infer<typeof obrigacaoDeDeducao>

export const contaPagar = z.object({
  id: uuid,
  codigo: z.string(),
  fornecedorCapacidadeId: uuid.nullable(),
  fornecedorNome: z.string(),
  fornecedorNatureza: z.enum(['PJ', 'PF', 'Avulso']),
  tipoPagamentoId: uuid,
  tipoPagamentoNome: z.string(),
  descricao: z.string(),
  competencia: dataNominal,
  agenda: agendaDaContaPagar,
  recorrencia: recorrenciaDeContaPagar.nullable(),
  valor: z.string(),
  moeda: codigoDeMoeda,
  contaContabilId: uuid,
  contaContabilNome: z.string(),
  /** O código da conta, para a âncora do Plano de Contas (D-136, DS-538). */
  contaContabilCodigo: z.string(),
  centroCustoId: uuid,
  centroCustoNome: z.string(),
  itemContabilId: uuid.nullable(),
  itemContabilNome: z.string().nullable(),
  canceladoEm: dataNominal.nullable(),
  cancelamentoMotivo: z.string().nullable(),
  parcelas: z.array(linhaDeContaPagar),
  rateio: z.array(rateioDaContaPagar),
  liquidacoes: z.array(liquidacaoDaContaPagar),
  obrigacoesDeDeducao: z.array(obrigacaoDeDeducao),
})
export type ContaPagar = z.infer<typeof contaPagar>

export const opcaoDeContaPagar = z.object({ id: uuid, nome: z.string(), extra: z.string().nullable().optional() })
export type OpcaoDeContaPagar = z.infer<typeof opcaoDeContaPagar>

/*
  O fornecedor da despesa NÃO vem aqui (EI-05, DS-535): a lista inteira de até 200
  habilitações saiu, e a 6.4 busca em `GET /contas-pagar/fornecedores`.
*/
export const opcoesDeContaPagar = z.object({
  tiposPagamento: z.array(opcaoDeContaPagar.extend({ extra: z.enum(['exige_nf', 'exige_rpa', 'sem_documento']) })),
  contasContabeis: z.array(opcaoDeContaPagar),
  centrosCusto: z.array(opcaoDeContaPagar),
  itensContabeis: z.array(opcaoDeContaPagar),
  contasBancarias: z.array(opcaoDeContaPagar),
  pedidos: z.array(opcaoDeContaPagar),
  campanhas: z.array(opcaoDeContaPagar.extend({ extra: uuid })),
})
export type OpcoesDeContaPagar = z.infer<typeof opcoesDeContaPagar>

/**
 * A busca do fornecedor da Nova despesa (6.4 · D-138, DS-535, DS-536).
 *
 * `q` casa nome, nome fantasia, documento e categoria, sem acento; `id` resolve UM
 * fornecedor (o valor que o seletor já tem). Só habilitação ativa, do ambiente da entidade.
 */
export const buscaDeFornecedoresDaDespesa = z.object({
  q: z.string().optional(),
  id: uuid.optional(),
})
export type BuscaDeFornecedoresDaDespesa = z.input<typeof buscaDeFornecedoresDaDespesa>

/**
 * Um fornecedor como a despesa o escolhe: o prazo padrão sugere o vencimento, e a conta
 * contábil sugerida sai da história da categoria (a mais usada nas despesas de
 * fornecedores da mesma categoria). Sem história, `null`: nunca um palpite.
 */
export const fornecedorDaDespesa = z.object({
  /** O uuid da capacidade, a mesma chave de `conta_pagar.fornecedor_capacidade_id`. */
  id: uuid,
  nome: z.string(),
  natureza: naturezaDeFornecedor,
  /** País da parte, para a tela formatar o documento (D-149). */
  pais: z.string(),
  /** O documento da parte, inteiro e normalizado: o CNPJ da empresa ou o documento da pessoa (D-149). */
  documento: z.string().nullable(),
  categoria: z.string().nullable(),
  prazoPagamentoDias: z.number().int().nullable(),
  contaContabilSugerida: z.object({ id: uuid, codigo: z.string(), nome: z.string() }).nullable(),
})
export type FornecedorDaDespesa = z.infer<typeof fornecedorDaDespesa>

/** Sem `meta.podeHabilitar`: o atalho da tela aparece pela permissão de criar fornecedor (EI-05). */
export const respostaDeFornecedoresDaDespesa = z.object({ data: z.array(fornecedorDaDespesa) })
export type RespostaDeFornecedoresDaDespesa = z.infer<typeof respostaDeFornecedoresDaDespesa>

export const entradaDeRegistroDeDocumento = z.object({ anexoId: uuid })
export type EntradaDeRegistroDeDocumento = z.infer<typeof entradaDeRegistroDeDocumento>

export const entradaDePagamentoDaContaPagar = z.object({
  data: dataNominal,
  valor: dinheiroPositivo,
  contaBancariaId: uuid,
  forma: z.enum(['pix', 'ted', 'boleto', 'dinheiro', 'outro']).nullable().optional(),
  diferencaTipo: tipoDeDiferenca.nullable().optional(),
  diferencaValor: dinheiroPositivo.nullable().optional(),
  diferencaMotivo: z.string().trim().min(1).nullable().optional(),
  taxaCambio: z.string().nullable().optional(),
  valorBase: dinheiroPositivo.nullable().optional(),
  /** Idempotência (D1): a mesma chave com o mesmo corpo devolve o MESMO pagamento. */
  chaveDeComando: chaveDeComando.optional(),
})
export type EntradaDePagamentoDaContaPagar = z.infer<typeof entradaDePagamentoDaContaPagar>

export const entradaDeCancelamentoDaContaPagar = z.object({ motivoId: uuid })
export type EntradaDeCancelamentoDaContaPagar = z.infer<typeof entradaDeCancelamentoDaContaPagar>

/** Job interno que transforma a prévia confirmada em CAPs-filhas idempotentes. */
export const FILA_MATERIALIZAR_CONTAS_PAGAR_RECORRENTES = 'dv-materializar-contas-pagar-recorrentes'
export const jobDeMaterializacaoDeContasPagar = z.object({
  contaPagarRaizId: uuid,
  entityId: uuid,
  parcelas: z.array(entradaDeParcelaPagar).min(1).max(36),
})
export type JobDeMaterializacaoDeContasPagar = z.infer<typeof jobDeMaterializacaoDeContasPagar>
